import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { X, Plus, AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from "../ui/alert";
import { analyzeCompanyServices, extractCompanyServices, generateCompanyDescription } from '../../services/openai';

const CompanyDataFields = {
  BASIC_INFO: [
    { 
      key: 'legalName', 
      label: 'Company Legal Name', 
      title: 'Official registered name of the company',
      type: 'text', 
      required: true 
    },
    { 
      key: 'companyDescription', 
      label: 'Company Short Description', 
      title: 'Brief description of company\'s main activities and business areas',
      type: 'textarea', 
      required: true 
    },
    { 
      key: 'registrationNumber', 
      label: 'Company Registration/Identification Number', 
      title: 'Official registration or identification number (e.g., VAT number, company ID)',
      type: 'text', 
      required: true 
    },
    { 
      key: 'registrationCountry', 
      label: 'Company Registration Country Code', 
      title: 'Two-letter country code where the company is registered (e.g., DE, FR, IT)',
      type: 'select', 
      required: true,
      options: [
        { value: 'AT', label: 'Austria (AT)' },
        { value: 'BE', label: 'Belgium (BE)' },
        { value: 'BG', label: 'Bulgaria (BG)' },
        { value: 'HR', label: 'Croatia (HR)' },
        { value: 'CY', label: 'Cyprus (CY)' },
        { value: 'CZ', label: 'Czech Republic (CZ)' },
        { value: 'DK', label: 'Denmark (DK)' },
        { value: 'EE', label: 'Estonia (EE)' },
        { value: 'FI', label: 'Finland (FI)' },
        { value: 'FR', label: 'France (FR)' },
        { value: 'DE', label: 'Germany (DE)' },
        { value: 'GR', label: 'Greece (GR)' },
        { value: 'HU', label: 'Hungary (HU)' },
        { value: 'IE', label: 'Ireland (IE)' },
        { value: 'IT', label: 'Italy (IT)' },
        { value: 'LV', label: 'Latvia (LV)' },
        { value: 'LT', label: 'Lithuania (LT)' },
        { value: 'LU', label: 'Luxembourg (LU)' },
        { value: 'MT', label: 'Malta (MT)' },
        { value: 'NL', label: 'Netherlands (NL)' },
        { value: 'PL', label: 'Poland (PL)' },
        { value: 'PT', label: 'Portugal (PT)' },
        { value: 'RO', label: 'Romania (RO)' },
        { value: 'SK', label: 'Slovakia (SK)' },
        { value: 'SI', label: 'Slovenia (SI)' },
        { value: 'ES', label: 'Spain (ES)' },
        { value: 'SE', label: 'Sweden (SE)' }
      ]
    }
  ],
  SIZE_INFO: [
    { key: 'employeeCount', label: 'Number of Employees', type: 'number' },
    { key: 'annualTurnover', label: 'Annual Turnover (EUR)', type: 'number' },
    { 
      key: 'companySize', 
      label: 'Company Size', 
      type: 'select',
      options: [
        { value: 'micro', label: 'Micro (< 10 employees)' },
        { value: 'small', label: 'Small (10-49 employees)' },
        { value: 'medium', label: 'Medium (50-249 employees)' },
        { value: 'large', label: 'Large (250+ employees)' }
      ]
    },
  ],
  SECTOR_INFO: [
    { 
      key: 'primarySector', 
      label: 'Primary Sector', 
      type: 'select',
      options: [
        { value: 'energy', label: 'Energy' },
        { value: 'transport', label: 'Transport' },
        { value: 'banking', label: 'Banking' },
        { value: 'financial', label: 'Financial Market Infrastructure' },
        { value: 'health', label: 'Healthcare' },
        { value: 'drinking_water', label: 'Drinking Water' },
        { value: 'digital_infrastructure', label: 'Digital Infrastructure' },
        { value: 'ict', label: 'ICT Service Management' },
        { value: 'public_admin', label: 'Public Administration' },
        { value: 'space', label: 'Space' },
        { value: 'postal', label: 'Postal and Courier' },
        { value: 'waste_management', label: 'Waste Management' },
        { value: 'chemicals', label: 'Chemical Production/Distribution' },
        { value: 'food', label: 'Food Production/Distribution' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'digital_providers', label: 'Digital Providers' },
        { value: 'research', label: 'Research' },
        { value: 'other', label: 'Other' }
      ]
    },
    { key: 'services', label: 'Services', type: 'services' }
  ]
};

const CompanyDataReview = ({ initialData, onComplete, onBack }) => {
  const [formData, setFormData] = useState(initialData || {});
  const [currentSection, setCurrentSection] = useState('BASIC_INFO');
  const [services, setServices] = useState([]);
  const [suggestedServices, setSuggestedServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [newService, setNewService] = useState({ name: '', description: '' });
  const [generatingDescription, setGeneratingDescription] = useState(false);

  const generateDescription = useCallback(async () => {
    if (!formData.legalName || generatingDescription) return;

    try {
      setGeneratingDescription(true);
      const result = await generateCompanyDescription(formData.legalName);
      
      if (result.confidence > 0.6) {
        setFormData(prev => ({
          ...prev,
          companyDescription: result.description
        }));
      }
    } catch (err) {
      console.error('Error generating description:', err);
      setError('Failed to generate company description');
    } finally {
      setGeneratingDescription(false);
    }
  }, [formData.legalName, generatingDescription]);

  const extractServicesAndSector = useCallback(async () => {
    try {
      setLoading(true);
      const result = await extractCompanyServices({
        company_name: formData.legalName,
        company_description: formData.companyDescription
      });

      setSuggestedServices(result.suggested_services);
      
      // Auto-select primary sector if confidence is high enough
      if (result.primary_sector?.confidence > 0.7) {
        setFormData(prev => ({
          ...prev,
          primarySector: result.primary_sector.name.toLowerCase().replace(/ /g, '_')
        }));
      }
    } catch (err) {
      console.error('Error extracting services:', err);
      setError('Failed to analyze company information');
    } finally {
      setLoading(false);
    }
  }, [formData.legalName, formData.companyDescription]);

  useEffect(() => {
    if (formData.legalName && !formData.companyDescription) {
      generateDescription();
    }
  }, [formData.legalName, formData.companyDescription, generateDescription]);

  useEffect(() => {
    if (formData.legalName && formData.companyDescription) {
      extractServicesAndSector();
    }
  }, [formData.legalName, formData.companyDescription, extractServicesAndSector]);

  useEffect(() => {
    // Update company size when employee count changes
    if (formData.employeeCount) {
      const count = parseInt(formData.employeeCount);
      let size = 'micro';
      if (count >= 250) size = 'large';
      else if (count >= 50) size = 'medium';
      else if (count >= 10) size = 'small';
      
      handleInputChange('companySize', size);
    }
  }, [formData.employeeCount]);

  const handleAddSuggestedService = async (service) => {
    try {
      setLoading(true);
      const analysis = await analyzeCompanyServices({
        company_name: formData.legalName,
        company_description: formData.companyDescription,
        main_services: [service.name]
      });

      setServices(prev => [...prev, {
        name: service.name,
        description: service.description,
        analysis: analysis[0]
      }]);

      // Remove from suggestions
      setSuggestedServices(prev => 
        prev.filter(s => s.name !== service.name)
      );
    } catch (err) {
      console.error('Error analyzing service:', err);
      setError('Failed to analyze service');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddService = async () => {
    if (newService.name && newService.description) {
      try {
        // Analyze the new service
        const analysis = await analyzeCompanyServices({
          company_name: formData.legalName,
          company_description: newService.description,
          main_services: [newService.name]
        });

        const serviceWithAnalysis = {
          ...newService,
          analysis: analysis[0] || null
        };

        setServices(prev => [...prev, serviceWithAnalysis]);
        setNewService({ name: '', description: '' });
        
        // Update parent component
        handleInputChange('services', [...services, serviceWithAnalysis]);
      } catch (error) {
        console.error('Error analyzing service:', error);
      }
    }
  };

  const handleRemoveService = (index) => {
    const updatedServices = services.filter((_, i) => i !== index);
    setServices(updatedServices);
    handleInputChange('services', updatedServices);
  };

  const renderField = (field) => {
    const value = formData[field.key] || '';

    return (
      <div key={field.key} className="space-y-2">
        <div className="space-y-1">
          <Label>{field.label}</Label>
          {field.title && (
            <p className="text-sm text-gray-500">{field.title}</p>
          )}
        </div>
        {field.type === 'select' ? (
          <Select
            value={value}
            onValueChange={(value) => handleInputChange(field.key, value)}
            disabled={field.key === 'companySize'}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : field.type === 'textarea' ? (
          <div className="relative">
            <Textarea
              value={value}
              onChange={(e) => handleInputChange(field.key, e.target.value)}
              placeholder={`Enter ${field.label.toLowerCase()}`}
              className="min-h-[100px]"
              disabled={generatingDescription}
            />
            {field.key === 'companyDescription' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={generateDescription}
                disabled={generatingDescription || !formData.legalName}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${generatingDescription ? 'animate-spin' : ''}`} />
                {generatingDescription ? 'Generating...' : 'Generate'}
              </Button>
            )}
          </div>
        ) : (
          <Input
            type={field.type}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        )}
      </div>
    );
  };

  const renderServiceAnalysis = (service) => {
    if (!service.analysis) return null;

    const getStatusColor = (assessment) => {
      if (assessment.applicability === 'yes') return 'bg-blue-50';
      if (assessment.applicability === 'maybe') return 'bg-yellow-50';
      return 'bg-gray-50';
    };

    const getStatusText = (assessment) => {
      if (assessment.applicability === 'yes') return 'Applicable';
      if (assessment.applicability === 'maybe') return 'Potentially Applicable';
      return 'Not Applicable';
    };

    const renderAssessment = (title, assessment, extraInfo) => (
      <div className={`p-4 rounded-lg ${getStatusColor(assessment)} mb-4`}>
        <h4 className="font-medium text-lg mb-2">{title}</h4>
        <div className="space-y-2">
          <p className="font-medium">Status: {getStatusText(assessment)}</p>
          {assessment.classification && (
            <p className="text-sm">
              <span className="font-medium">Classification: </span>
              {assessment.classification.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              {assessment.classification_reason && (
                <span className="block text-gray-600 mt-1">{assessment.classification_reason}</span>
              )}
            </p>
          )}
          <p className="text-sm">
            <span className="font-medium">Justification: </span>
            {assessment.justification}
          </p>
          {extraInfo}
        </div>
      </div>
    );

    return (
      <div className="mt-4 space-y-4">
        {/* NIS2 Assessment */}
        {renderAssessment(
          'NIS2 Assessment',
          service.analysis.nis2_assessment
        )}

        {/* DORA Assessment */}
        {renderAssessment(
          'DORA Assessment',
          service.analysis.dora_assessment
        )}

        {/* GDPR Assessment */}
        {renderAssessment(
          'GDPR Assessment',
          service.analysis.gdpr_assessment
        )}
      </div>
    );
  };

  const renderServiceForm = () => (
    <div className="p-4 border rounded-lg bg-gray-50">
      <div className="grid gap-4">
        <div>
          <Label>Service Name</Label>
          <Input
            value={newService.name}
            onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Enter service name"
            className="mt-1"
          />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea
            value={newService.description}
            onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Describe the service..."
            className="w-full min-h-[100px] p-2 border rounded-md mt-1"
          />
        </div>
        <Button
          onClick={handleAddService}
          disabled={!newService.name || !newService.description}
          className="w-full"
        >
          <Plus size={16} className="mr-2" /> Add Service
        </Button>
      </div>
    </div>
  );

  const renderSection = (sectionKey) => {
    const fields = CompanyDataFields[sectionKey];
    
    if (!fields) return null;
    
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{sectionKey.replace('_', ' ')}</h3>
        {fields.map(field => 
          field.type === 'services' ? (
            <div key={field.key} className="space-y-4">
              {/* Suggested Services */}
              {currentSection === 'SECTOR_INFO' && suggestedServices.length > 0 && (
                <div className="space-y-2">
                  <Label>Suggested Services</Label>
                  <div className="space-y-2">
                    {suggestedServices.map((service, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                        <div>
                          <p className="font-medium">{service.name}</p>
                          <p className="text-sm text-gray-600">{service.description}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddSuggestedService(service)}
                          disabled={loading}
                        >
                          Add Service
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Added Services */}
              <div className="space-y-4">
                {services.map((service, index) => (
                  <div key={index} className="border rounded-lg bg-white">
                    <div className="p-4 relative">
                      <button
                        onClick={() => handleRemoveService(index)}
                        className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                      >
                        <X size={16} />
                      </button>
                      <h4 className="font-medium text-lg">{service.name}</h4>
                      <p className="text-gray-600 mt-1">{service.description}</p>
                      {renderServiceAnalysis(service)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Service Button */}
              <Button
                variant="outline"
                onClick={() => setShowServiceForm(true)}
                className="w-full"
                disabled={loading}
              >
                <Plus className="mr-2 h-4 w-4" /> Add Custom Service
              </Button>

              {showServiceForm && renderServiceForm()}
            </div>
          ) : renderField(field)
        )}
      </div>
    );
  };

  const handleSectionComplete = () => {
    if (currentSection === 'BASIC_INFO') setCurrentSection('SIZE_INFO');
    else if (currentSection === 'SIZE_INFO') setCurrentSection('SECTOR_INFO');
    else {
      onComplete(formData);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 bg-white rounded-lg shadow">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-2">
          {Object.keys(CompanyDataFields).map((section) => (
            <span
              key={section}
              className={`cursor-pointer ${
                currentSection === section ? 'text-blue-600 font-medium' : 'text-gray-500'
              }`}
              onClick={() => setCurrentSection(section)}
            >
              {section.replace('_', ' ')}
            </span>
          ))}
        </div>
        <div className="h-2 bg-gray-200 rounded-full">
          <div
            className="h-full bg-blue-600 rounded-full transition-all duration-300"
            style={{
              width: `${
                (Object.keys(CompanyDataFields).indexOf(currentSection) + 1) * 50
              }%`,
            }}
          />
        </div>
      </div>

      {/* Current Section */}
      {renderSection(currentSection)}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={() => {
            if (currentSection === 'SIZE_INFO') setCurrentSection('BASIC_INFO');
            else if (currentSection === 'SECTOR_INFO') setCurrentSection('SIZE_INFO');
            else onBack();
          }}
          disabled={currentSection === 'BASIC_INFO'}
        >
          Previous
        </Button>
        <Button onClick={handleSectionComplete}>
          {currentSection === 'SECTOR_INFO' ? 'Complete' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default CompanyDataReview;
